import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaHandshake, FaMoneyBillWave, FaTools, FaShieldAlt, FaSearch, FaClock, FaCheckCircle, FaStar } from 'react-icons/fa';
import { Link } from 'react-router-dom';

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(145deg, #f8fafa 0%, #e6f0f0 100%);
  padding: 6rem 2rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 30%, rgba(0, 128, 128, 0.05) 0%, transparent 70%),
      radial-gradient(circle at 80% 70%, rgba(32, 178, 170, 0.05) 0%, transparent 70%);
    pointer-events: none;
  }
`;

const ContentWrapper = styled(motion.div)`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const Header = styled(motion.div)`
  text-align: center;
  margin-bottom: 6rem;
`;

const Title = styled(motion.h1)`
  font-size: clamp(2.5rem, 5vw, 4rem);
  color: #2A4747;
  margin-bottom: 1.5rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.2;
  text-align: center;

  span {
    display: block;
    background: linear-gradient(135deg, #008080 0%, #20B2AA 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    font-size: clamp(2rem, 4vw, 3rem);
    margin-top: 0.5rem;
  }
`;

const Description = styled(motion.p)`
  font-size: clamp(1.1rem, 2vw, 1.3rem);
  color: #5f7777;
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.6;
  text-align: center;
  margin-bottom: 2rem;
`;

const BenefitsGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2.5rem;
  margin-top: 4rem;
`;

const BenefitCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 24px;
  padding: 2.5rem;
  box-shadow: 0 20px 40px rgba(0, 128, 128, 0.1);
  border: 1px solid rgba(0, 128, 128, 0.1);
  transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(120deg, transparent, rgba(0, 128, 128, 0.03), transparent);
    transform: translateX(-100%);
    transition: transform 0.6s ease;
  }

  &:hover::before {
    transform: translateX(100%);
  }
`;

const IconWrapper = styled(motion.div)`
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, #008080 0%, #20B2AA 100%);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  color: white;
  font-size: 2rem;
  box-shadow: 0 10px 20px rgba(0, 128, 128, 0.15);
`;

const BenefitTitle = styled.h3`
  font-size: 1.5rem;
  color: #2A4747;
  margin-bottom: 1rem;
  font-weight: 600;
`;

const BenefitDescription = styled.p`
  color: #5f7777;
  line-height: 1.6;
  font-size: 1.1rem;
`;

const SavingsHighlight = styled(motion.div)`
  background: linear-gradient(135deg, rgba(0, 128, 128, 0.1) 0%, rgba(32, 178, 170, 0.1) 100%);
  border-radius: 16px;
  padding: 1.5rem;
  margin-top: 1.5rem;
  border: 1px solid rgba(0, 128, 128, 0.1);

  span {
    color: #008080;
    font-weight: 600;
  }
`;

const CTAButton = styled(motion(Link))`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, #008080 0%, #20B2AA 100%);
  color: white;
  padding: 1rem 2rem;
  border-radius: 100px;
  text-decoration: none;
  font-weight: 600;
  font-size: 1.1rem;
  margin-top: 2rem;
  box-shadow: 0 10px 20px rgba(0, 128, 128, 0.15);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 15px 30px rgba(0, 128, 128, 0.2);
  }
`;

const Skills = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.8, staggerChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  const benefits = [
    {
      icon: <FaSearch />,
      title: "Find Verified Specialists",
      description: "Access our network of pre-screened, skilled subcontractors. Each specialist is thoroughly vetted and maintains a high rating.",
      savings: "Save time finding the right professional"
    },
    {
      icon: <FaMoneyBillWave />,
      title: "Save Up to 30%",
      description: "Get direct pricing from subcontractors without general contractor markups. Transparent pricing with no hidden fees.",
      savings: "Average savings of $3,000 per project"
    },
    {
      icon: <FaClock />,
      title: "Instant Estimates",
      description: "Get immediate project estimates and connect with available subcontractors in your area within minutes.",
      savings: "Book projects up to 3x faster"
    },
    {
      icon: <FaShieldAlt />,
      title: "100% Protected",
      description: "Every project is backed by our $2M liability coverage and satisfaction guarantee. Work with confidence.",
      savings: "Risk-free hiring process"
    },
    {
      icon: <FaCheckCircle />,
      title: "Quality Assurance",
      description: "All work is inspected and must meet our high standards. Only the top 10% of subcontractors qualify for our network.",
      savings: "Premium quality guaranteed"
    },
    {
      icon: <FaStar />,
      title: "Verified Reviews",
      description: "Read authentic reviews from real homeowners. Our specialists maintain an average rating of 4.8/5 stars.",
      savings: "Make confident decisions"
    },
    {
      icon: <FaTools />,
      title: "All Services Covered",
      description: "From outdoor kitchens to complete landscaping, find specialists for any outdoor project all in one place.",
      savings: "One-stop solution for all projects"
    },
    {
      icon: <FaHandshake />,
      title: "Dedicated Support",
      description: "Get personalized assistance throughout your project. Our team is available 7 days a week to help.",
      savings: "Expert guidance included"
    }
  ];

  return (
    <Container>
      <ContentWrapper
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <Header>
          <Title variants={itemVariants}>
            Transform Your Outdoor Space
            <span>With Top-Rated Specialists</span>
          </Title>
          <Description variants={itemVariants}>
            Connect directly with skilled subcontractors, save up to 30% on your project, 
            and get the quality results you deserve. No middlemen, no markups - just exceptional work at fair prices.
          </Description>
          <CTAButton
            to="/calculator"
            as={motion.a}
            variants={itemVariants}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Get Instant Estimate →
          </CTAButton>
        </Header>

        <BenefitsGrid>
          {benefits.map((benefit, index) => (
            <BenefitCard
              key={index}
              variants={itemVariants}
              whileHover={{ y: -5, boxShadow: '0 25px 50px rgba(0, 128, 128, 0.15)' }}
            >
              <IconWrapper
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                {benefit.icon}
              </IconWrapper>
              <BenefitTitle>{benefit.title}</BenefitTitle>
              <BenefitDescription>{benefit.description}</BenefitDescription>
              <SavingsHighlight
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
              >
                <span>✨ {benefit.savings}</span>
              </SavingsHighlight>
            </BenefitCard>
          ))}
        </BenefitsGrid>

        <motion.div
          variants={itemVariants}
          style={{ textAlign: 'center', marginTop: '4rem' }}
        >
          <CTAButton
            to="/calculator"
            as={motion.a}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Start Your Project Now →
          </CTAButton>
        </motion.div>
      </ContentWrapper>
    </Container>
  );
};

export default Skills;
